/**
 * The renderer's prefab catalog (ADR 0031): one module per prefab id of
 * `scenePrefabIds` (`@ab/content-schema`), each exporting `prefab`, a
 * `buildPrefab(ctx, params)` (`prefabs/types.ts`). `test/catalog.test.ts`
 * asserts this catalog is exactly the schema's list, so content lint can
 * check a placement against the list without importing the renderer.
 */
import type { ScenePrefabId } from '@ab/content-schema';
import { prefab as houseApothecary } from './prefabs/house-apothecary.ts';
import { prefab as houseMeridian } from './prefabs/house-meridian.ts';
import { prefab as houseWest } from './prefabs/house-west.ts';
import { prefab as houseRiver } from './prefabs/house-river.ts';
import { prefab as houseEast } from './prefabs/house-east.ts';
import { prefab as houseMarket } from './prefabs/house-market.ts';
import { prefab as bridge } from './prefabs/bridge.ts';
import { prefab as streetTree } from './prefabs/street-tree.ts';
import { prefab as cargo } from './prefabs/cargo.ts';
import { prefab as quayLamp } from './prefabs/quay-lamp.ts';
import { prefab as bench } from './prefabs/bench.ts';
import { prefab as bellTower } from './prefabs/bell-tower.ts';
import { prefab as fountainCourt } from './prefabs/fountain-court.ts';
import { prefab as marketStall } from './prefabs/market-stall.ts';
import { prefab as marketCart } from './prefabs/market-cart.ts';
import { prefab as fishingRod } from './prefabs/fishing-rod.ts';
import { prefab as shrubScatter } from './prefabs/shrub-scatter.ts';
import { prefab as leafLitter } from './prefabs/leaf-litter.ts';
import { prefab as streetBunting } from './prefabs/street-bunting.ts';
import { prefab as riverReeds } from './prefabs/river-reeds.ts';
import { prefab as skiff } from './prefabs/skiff.ts';
import { prefab as crateStack } from './prefabs/crate-stack.ts';
import { prefab as distantHills } from './prefabs/distant-hills.ts';
import { prefab as riverBanks } from './prefabs/river-banks.ts';
import { prefab as perimeterRail } from './prefabs/perimeter-rail.ts';
import { prefab as mooringFinger } from './prefabs/mooring-finger.ts';
import { prefab as boatLanding } from './prefabs/boat-landing.ts';
import { prefab as quayFittings } from './prefabs/quay-fittings.ts';
import { prefab as backdropTown } from './prefabs/backdrop-town.ts';
import { prefab as backdropSouth } from './prefabs/backdrop-south.ts';
import { prefab as backdropWest } from './prefabs/backdrop-west.ts';
import { prefab as northernCrossing } from './prefabs/northern-crossing.ts';
import { prefab as easternGarden } from './prefabs/eastern-garden.ts';
import { prefab as airshipMooring } from './prefabs/airship-mooring.ts';
import { prefab as shopRoom } from './prefabs/shop-room.ts';
import { prefab as shopShelf } from './prefabs/shop-shelf.ts';
import { prefab as shopCounter } from './prefabs/shop-counter.ts';
import { prefab as shopWallDecor } from './prefabs/shop-wall-decor.ts';
import { prefab as dryingHerbs } from './prefabs/drying-herbs.ts';
import { prefab as shopRug } from './prefabs/shop-rug.ts';
import { prefab as worktable } from './prefabs/worktable.ts';
import { prefab as hearth } from './prefabs/hearth.ts';
import { prefab as chandelier } from './prefabs/chandelier.ts';
import { prefab as barrel } from './prefabs/barrel.ts';
import { prefab as crate } from './prefabs/crate.ts';
import { prefab as planter } from './prefabs/planter.ts';
import { prefab as exitMat } from './prefabs/exit-mat.ts';
import type { Prefab } from './prefabs/types.ts';
import { shipPrefabs } from '../ship/prefabs.ts';
import type { ShipPrefab } from '../ship/types.ts';

/** A town's prefab (`buildPrefab` with the town's kit) or the airship's (`build` with the ship's kit, ADR 0033). */
export type CatalogEntry = Prefab | ShipPrefab;

export const prefabCatalog: Readonly<Record<ScenePrefabId, CatalogEntry>> = {
  house_apothecary: houseApothecary,
  house_meridian: houseMeridian,
  house_west: houseWest,
  house_river: houseRiver,
  house_east: houseEast,
  house_market: houseMarket,
  bridge: bridge,
  street_tree: streetTree,
  cargo: cargo,
  quay_lamp: quayLamp,
  bench: bench,
  bell_tower: bellTower,
  fountain_court: fountainCourt,
  market_stall: marketStall,
  market_cart: marketCart,
  fishing_rod: fishingRod,
  shrub_scatter: shrubScatter,
  leaf_litter: leafLitter,
  street_bunting: streetBunting,
  river_reeds: riverReeds,
  skiff: skiff,
  crate_stack: crateStack,
  distant_hills: distantHills,
  river_banks: riverBanks,
  perimeter_rail: perimeterRail,
  mooring_finger: mooringFinger,
  boat_landing: boatLanding,
  quay_fittings: quayFittings,
  backdrop_town: backdropTown,
  backdrop_south: backdropSouth,
  backdrop_west: backdropWest,
  northern_crossing: northernCrossing,
  eastern_garden: easternGarden,
  airship_mooring: airshipMooring,
  shop_room: shopRoom,
  shop_shelf: shopShelf,
  shop_counter: shopCounter,
  shop_wall_decor: shopWallDecor,
  drying_herbs: dryingHerbs,
  shop_rug: shopRug,
  worktable: worktable,
  hearth: hearth,
  chandelier: chandelier,
  barrel: barrel,
  crate: crate,
  planter: planter,
  exit_mat: exitMat,
  ...shipPrefabs,
};

/** The town prefab of an id; null for a ship prefab (a town or interior never places one: content lint). */
export const townPrefab = (id: ScenePrefabId): Prefab | null => {
  const entry = prefabCatalog[id];
  return 'ship' in entry ? null : entry;
};

/** The ship prefab of an id; null for a town prefab. */
export const shipPrefab = (id: ScenePrefabId): ShipPrefab | null => {
  const entry = prefabCatalog[id];
  return 'ship' in entry ? entry : null;
};
